import { useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { StorageLocationAutoComplete } from '@/components/common/storage-location-auto-complete';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { StorageLocation } from '@/rtk-query.gql';
import { useEditSemen } from '@/services/semen-service/hooks';
import { EditSemenInput, SemenDto } from '@/services/semen-service/types';

type EditLocationDialogProps = {
  visible: boolean;
  onHide: () => void;
  selectedSemen: SemenDto[];
  gridRef?: React.RefObject<AgGridReact>;
  onFilterChanged?: () => void;
};

export const EditLocationDialog = ({
  visible,
  onHide,
  selectedSemen,
  gridRef,
  onFilterChanged,
}: EditLocationDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedStorageLocation, setSelectedStorageLocation] = useState<
    StorageLocation | undefined
  >(undefined);

  const { mutateAsync: editSemenMutation, isPending: isEditingSemen } =
    useEditSemen();

  const handleSave = async () => {
    if (!selectedStorageLocation) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('storageLocation.storageLocationRequired'),
          detail: t('storageLocation.selectStorageLocation'),
          life: ToastLife.DEFAULT,
        }),
      );
      return;
    }

    if (selectedSemen.length === 0) {
      return;
    }

    const editInputs: EditSemenInput[] = selectedSemen.map(
      (semen: SemenDto) => ({
        semenId: semen.id,
        storageLocationId: selectedStorageLocation.id,
        storageLocationName: selectedStorageLocation.name ?? undefined,
        previousStorageLocationId: semen.storageLocation?.id,
        previousStorageLocationName: semen.storageLocation?.name,
        transactionType: 'Transfer',
        cane: undefined,
        center: undefined,
        colCode: undefined,
        cssCertified: undefined,
        dateReceived: undefined,
        naab: undefined,
        export: undefined,
        ownerId: undefined,
        ownerName: undefined,
        previousOwnerId: undefined,
        previousOwnerName: undefined,
        quantity: undefined,
        quantityRec: undefined,
        sex: undefined,
        sexed: undefined,
        shortName: undefined,
        sireId: undefined,
        storageLocation: undefined,
      }),
    );

    try {
      const results = await editSemenMutation(editInputs);
      const failedResults = results.filter((r) => r.success === false);

      if (failedResults.length > 0) {
        failedResults.forEach((result) => {
          dispatch(
            addToast({
              severity: 'warn',
              summary: t('common.error'),
              detail:
                result.message ||
                t('storageLocation.failedToUpdateSemenRecords'),
              life: ToastLife.MEDIUM,
            }),
          );
        });
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('storageLocation.storageLocationUpdated', {
              count: selectedSemen?.length || 0,
            }),
            life: ToastLife.MEDIUM,
          }),
        );
      }

      if (gridRef && gridRef.current?.api) {
        gridRef.current.api.deselectAll();

        setTimeout(() => {
          if (gridRef && gridRef.current?.api) {
            gridRef.current.api.purgeInfiniteCache();
            if (onFilterChanged) {
              onFilterChanged();
            }
          }
        }, 200);
      }

      setSelectedStorageLocation(undefined);
      onHide();
    } catch (_error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('storageLocation.failedToUpdateStorageLocation'),
          life: ToastLife.MEDIUM,
        }),
      );
    }
  };

  const handleClose = () => {
    setSelectedStorageLocation(undefined);
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header={`${selectedSemen.length > 1 ? t('common.bulkEdit') : t('common.edit')} ${t('storageLocation.storageLocation')}`}
      style={{ width: '50rem' }}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            label={t('common.cancel')}
            className="p-button-text"
            onClick={handleClose}
          />
          <Button
            label={t('common.save')}
            icon="pi pi-check"
            onClick={handleSave}
            disabled={!selectedStorageLocation || isEditingSemen}
            loading={isEditingSemen}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded border border-gray-300 bg-gray-50 p-3">
          <div className="mb-2 font-semibold text-gray-700">
            {selectedSemen.length > 1
              ? t('semen.selectedSemenRecords', { count: selectedSemen.length })
              : t('semen.selectedSemen')}
          </div>
          {selectedSemen.length === 1 ? (
            <div className="text-sm text-gray-600">
              <div>
                <strong>{t('sire.sire')}:</strong>{' '}
                {selectedSemen[0]?.sire?.name || 'N/A'}
              </div>
              <div>
                <strong>{t('semen.naab')}:</strong>{' '}
                {selectedSemen[0]?.naab || 'N/A'}
              </div>
              <div>
                <strong>{t('storageLocation.storageLocation')}:</strong>{' '}
                {selectedSemen[0]?.storageLocation?.name || 'N/A'}
              </div>
              {selectedSemen[0]?.quantity !== undefined && (
                <div>
                  <strong>{t('semen.quantity')}:</strong>{' '}
                  {selectedSemen[0].quantity}
                </div>
              )}
            </div>
          ) : (
            <div className="max-h-40 space-y-2 overflow-y-auto text-sm text-gray-600">
              {selectedSemen.map((semen, index) => (
                <div
                  key={semen.id || index}
                  className="border-b border-gray-200 pb-2 last:border-b-0"
                >
                  <div>
                    <strong>{t('sire.sire')}:</strong>{' '}
                    {semen.sire?.name || 'N/A'} |{' '}
                    <strong>{t('semen.naab')}:</strong> {semen.naab || 'N/A'}
                  </div>
                  <div>
                    <strong>{t('storageLocation.storageLocation')}:</strong>{' '}
                    {semen.storageLocation?.name || 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-semibold text-gray-700">
            {t('storageLocation.newStorageLocation')}
          </label>
          <StorageLocationAutoComplete
            defaultValue={selectedStorageLocation}
            field="name"
            onSelect={(e: AutoCompleteSelectEvent) =>
              setSelectedStorageLocation(e.value)
            }
            className="w-full"
            appendTo={null}
          />
        </div>
      </div>
    </Dialog>
  );
};
